import { nip19 } from 'nostr-tools';

/** A person the user follows, normalized to bech32 + hex forms. */
export interface FollowedUser {
  npub: string;
  pubkey: string;
  addedAt: number;
}

/** Content switches mirrored by the worker's query parameters. */
export interface FeedOptions {
  notes: boolean;
  articles: boolean;
  replies: boolean;
}

export const DEFAULT_FEED_OPTIONS: FeedOptions = {
  notes: true,
  articles: true,
  replies: false,
};

/** Keep in sync with MAX_NPUBS in worker.ts. */
export const MAX_FOLLOWS = 25;

/**
 * Parse user input into a normalized identity.
 * Accepts npub, nprofile, `nostr:` URIs, and raw hex pubkeys.
 */
export function parseNpubInput(input: string): { npub: string; pubkey: string } | null {
  const value = input.trim().replace(/^nostr:/i, '');
  if (!value) return null;

  if (/^[0-9a-f]{64}$/i.test(value)) {
    const pubkey = value.toLowerCase();
    return { npub: nip19.npubEncode(pubkey), pubkey };
  }

  try {
    const decoded = nip19.decode(value);
    if (decoded.type === 'npub') {
      return { npub: nip19.npubEncode(decoded.data), pubkey: decoded.data };
    }
    if (decoded.type === 'nprofile') {
      return { npub: nip19.npubEncode(decoded.data.pubkey), pubkey: decoded.data.pubkey };
    }
  } catch {
    // Invalid bech32 falls through to null.
  }
  return null;
}

/**
 * Build a feed URL. Query params are only included when they differ from the
 * worker defaults (both kinds on, replies off) to keep URLs short and readable.
 */
export function buildFeedUrl(origin: string, npubs: string[], options: FeedOptions): string {
  let url = `${origin}/feed.xml?npubs=${npubs.join(',')}`;

  if (!(options.notes && options.articles)) {
    const kinds = [options.notes ? 'notes' : '', options.articles ? 'articles' : '']
      .filter(Boolean)
      .join(',');
    url += `&kinds=${kinds}`;
  }
  if (options.replies) {
    url += '&replies=1';
  }
  return url;
}

/** Generate an OPML subscription list with one outline per followed person. */
export function buildOpml(
  users: { name: string; npub: string }[],
  origin: string,
  options: FeedOptions,
): string {
  const outlines = users
    .map(({ name, npub }) => {
      const xmlUrl = buildFeedUrl(origin, [npub], options);
      const htmlUrl = `https://njump.me/${npub}`;
      return `    <outline text="${escapeXml(name)}" title="${escapeXml(name)}" type="rss" xmlUrl="${escapeXml(xmlUrl)}" htmlUrl="${escapeXml(htmlUrl)}" />`;
    })
    .join('\n');

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<opml version="2.0">',
    '  <head>',
    '    <title>NostrRSS subscriptions</title>',
    `    <dateCreated>${new Date().toUTCString()}</dateCreated>`,
    '  </head>',
    '  <body>',
    outlines,
    '  </body>',
    '</opml>',
  ].join('\n');
}

/** Download a string as a file in the browser. */
export function downloadFile(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function escapeXml(value: string): string {
  return value.replace(/[<>&'"]/g, (char) => {
    switch (char) {
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '&':
        return '&amp;';
      case "'":
        return '&apos;';
      default:
        return '&quot;';
    }
  });
}

/** Compact relative timestamp, e.g. "5m", "3h", "2d". */
export function timeAgo(unixSeconds: number): string {
  const elapsed = Math.max(0, Math.floor(Date.now() / 1000 - unixSeconds));
  if (elapsed < 60) return `${elapsed}s`;
  if (elapsed < 3600) return `${Math.floor(elapsed / 60)}m`;
  if (elapsed < 86400) return `${Math.floor(elapsed / 3600)}h`;
  if (elapsed < 604800) return `${Math.floor(elapsed / 86400)}d`;
  return new Date(unixSeconds * 1000).toLocaleDateString();
}
